import { getShortId } from "@/lib/string";
import { classNames } from "@/lib/utils";
import IconButton from "@components/library/IconButton";
import { hlToast } from "@components/library/Toast";
import { ToastVariant } from "@components/library/Toast";
import { EntityIconType, getIconComponentByType } from "@components/library/Entities/EntityIcon";
import { ClickToCopyWrapper } from "@components/library/IdDisplay";
import Tooltip from "@components/library/Tooltip";
import { LinkIcon } from "@heroicons/react/outline";

const DrawerEntityIcon = ({ entityType, className }: { entityType: EntityIconType; className?: string }) => {
  const IconComponent = getIconComponentByType(entityType);
  return <IconComponent weight="fill" className={classNames("h-14 w-14", className)} />;
};

/**
 * This component appears at the top left of a drawer
 */
export const DrawerTitle = ({
  title,
  entityType,
  entityId,
  copyText,
  linkToCopy,
  copyLinkTooltip,
}: {
  title: string;
  entityType: EntityIconType;
  entityId: string;
  /**
   * This is something like 'Session ID', 'Version ID', 'File ID', etc.
   */
  copyText?: string;
  /**
   * Deep link for the entity.
   */
  linkToCopy?: string;
  /**
   * Tooltip for the link to copy—this is the link to deep link to the drawer (for logs and versions)
   */
  copyLinkTooltip?: string;
}) => {
  return (
    <div className="flex items-center gap-12 pl-16">
      {/* Title section with Entity Icon + Title */}
      <div className="flex items-center gap-6">
        <DrawerEntityIcon entityType={entityType} className="text-text-base-3" />
        <span className="text-12-12 font-medium text-text-base-3">{title}</span>
      </div>

      {/* ID section with short ID + Link Icon */}
      <div className="flex items-center gap-6">
        <Tooltip content={`Copy ${copyText ?? "ID"}`}>
          <div
            className="flex items-center"
            onClick={() => {
              navigator.clipboard.writeText(entityId);
              hlToast({
                title: `Copied ${copyText ?? "ID"} to clipboard`,
                variant: ToastVariant.Success,
              });
            }}
          >
            <span className="cursor-pointer font-mono text-12-12 text-text-base-3">{getShortId(entityId)}</span>
          </div>
        </Tooltip>
        {linkToCopy && (
          <Tooltip content={copyLinkTooltip ?? "Copy Link"}>
            <IconButton
              Icon={LinkIcon}
              size={24}
              shade={"gray"}
              onClick={() => {
                // Just copy current URL to clipboard
                navigator.clipboard.writeText(linkToCopy).then(() => {
                  hlToast({
                    variant: ToastVariant.Success,
                    title: "Link copied to clipboard",
                  });
                });
              }}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
};
